import React, { useRef, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import {
  PAYWALL_CONTENT_MAX_WIDTH_CLASS,
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_LINKS,
  PAYWALL_PLAN_FEATURES,
  PAYWALL_TESTIMONIALS,
  PAYWALL_TESTIMONIALS_PER_PAGE,
  PAYWALL_TRUST_BADGES,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallDivider,
  PaywallPdfPage,
  PaywallPriceText,
} from './components';
import { ChevronIcon, CircleCheckIcon, StarIcon, StepCheckIcon } from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { splitFileName } from './utils';

const PriceText = ({ value }: { value: string }) => (
  <PaywallPriceText
    value={value}
    className="leading-[30px] text-[#292A33]"
    supClassName="relative -top-[0.35em] ml-px text-[16.125px]"
  />
);

const TotalDueRow = ({ amountText }: { amountText: string }) => (
  <div className="flex w-full items-center justify-between whitespace-nowrap">
    <span className="text-[20px] font-semibold text-[#292A33]">
      Total due today:
    </span>
    <PriceText value={amountText} />
  </div>
);

const Step = ({
  state,
  label,
  index,
}: {
  state: 'complete' | 'active' | 'inactive';
  label: string;
  index?: number;
}) => (
  <div className="flex items-center gap-3">
    <span
      className={clsx(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[17px] leading-none',
        state === 'complete' && 'bg-[#292A33] text-white',
        state === 'active' && 'bg-[#4B7EFF] text-white',
        state === 'inactive' && 'bg-[#EEEEF2] text-[#787887]'
      )}
    >
      {state === 'complete' ? (
        <StepCheckIcon className="h-4 w-4" aria-hidden />
      ) : (
        index
      )}
    </span>
    <span
      className={clsx(
        'whitespace-nowrap text-[17px]',
        state === 'complete' && 'text-[#292A33]',
        state === 'active' && 'text-[#4B7EFF]',
        state === 'inactive' && 'text-[#878787]'
      )}
    >
      {label}
    </span>
  </div>
);

const StepLine = () => <span className="h-px w-8 shrink-0 bg-[#BFBFBF]" />;

const DocumentPreview = ({
  file,
  extension,
}: {
  file?: File | null;
  extension: string;
}) => (
  <div className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-[8px] bg-[#E5E6EB]">
    <div className="h-20 w-[57px] overflow-hidden bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.16)]">
      {file?.type === 'application/pdf' && (
        <PaywallPdfPage
          file={file}
          width={57}
          fallback={<span className="block h-full w-full bg-white" />}
        />
      )}
    </div>
    <span
      className={clsx(
        'pointer-events-none absolute left-[11px] top-3 rounded-[6px] border-2 border-white p-1 text-[10px] font-medium uppercase leading-3 text-white',
        PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400'
      )}
    >
      {extension}
    </span>
  </div>
);

const DocumentReadyCard = ({
  documentName,
  documentExtension,
  documentFile,
}: {
  documentName: string;
  documentExtension: string;
  documentFile?: File | null;
}) => {
  const { base, suffix } = splitFileName(documentName, documentExtension);

  return (
    <div className="flex w-full items-center justify-between gap-4 rounded-[16px] bg-[#DAF6ED] py-3 pl-6 pr-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[18px] font-semibold leading-5 text-[#292A33]">
          Your document is ready!
        </span>
        <p
          title={documentName}
          className="flex min-w-0 text-[14px] font-medium leading-5 text-[#292A33]"
        >
          <span className="-mr-px min-w-0 truncate">{base}</span>
          <span className="shrink-0 text-[#4B7EFF]">{suffix}</span>
        </p>
      </div>
      <DocumentPreview file={documentFile} extension={documentExtension} />
    </div>
  );
};

const Testimonials = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(
    PAYWALL_TESTIMONIALS.length / PAYWALL_TESTIMONIALS_PER_PAGE
  );

  const goToPage = (nextPage: number) => {
    const track = trackRef.current;
    if (!track) return;

    const clamped = Math.min(Math.max(nextPage, 0), pageCount - 1);

    isSyncingRef.current = true;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      isSyncingRef.current = false;
    }, 600);

    track.scrollTo({
      left: (track.scrollWidth / pageCount) * clamped,
      behavior: 'smooth',
    });
    setPage(clamped);
  };

  const handleScroll = () => {
    if (isSyncingRef.current) return;

    const track = trackRef.current;
    if (!track) return;

    setPage(Math.round(track.scrollLeft / (track.scrollWidth / pageCount)));
  };

  return (
    <section
      className={`mx-auto flex w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} flex-col items-center gap-4 px-4 pb-6 lg:gap-8 lg:pb-8`}
    >
      <h2 className="text-center text-[28px] font-medium text-[#292A33] lg:text-[36px]">
        What our users say
      </h2>

      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex w-full snap-x snap-mandatory gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {PAYWALL_TESTIMONIALS.map(({ name, comment, date }) => (
          <article
            key={name}
            className="flex w-[256px] shrink-0 snap-start flex-col gap-4 rounded-[24px] border border-[rgba(0,161,112,0.24)] bg-white p-6 drop-shadow-[0px_8px_8px_rgba(0,161,112,0.1)] lg:w-[400px] lg:p-8"
          >
            <p className="text-[17px] font-semibold text-[#292A33]">{name}</p>
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <span
                  key={index}
                  className="flex h-6 w-6 items-center justify-center rounded-[4px] bg-[#00A170]"
                >
                  <StarIcon className="h-4 w-4 text-white" aria-hidden />
                </span>
              ))}
            </div>
            <p className="text-[17px] text-[#292A33]">{comment}</p>
            <p className="mt-auto text-[17px] text-[rgba(109,109,109,0.84)]">
              {date}
            </p>
          </article>
        ))}
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label="Previous reviews"
          disabled={page === 0}
          onClick={() => goToPage(page - 1)}
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-[8px]',
            page === 0
              ? 'bg-[#EEEEF2] text-[#CECFD4]'
              : 'bg-[#E5E6EB] text-[#292A33]'
          )}
        >
          <ChevronIcon className="h-6 w-6 -scale-x-100" aria-hidden />
        </button>
        <div className="flex items-center gap-2">
          {Array.from({ length: pageCount }).map((_, index) => (
            <span
              key={index}
              className={clsx(
                'h-1 w-6 rounded-[4px]',
                index === page ? 'bg-[#292A33]' : 'bg-[#CBCCD7]'
              )}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="Next reviews"
          disabled={page === pageCount - 1}
          onClick={() => goToPage(page + 1)}
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-[8px]',
            page === pageCount - 1
              ? 'bg-[#EEEEF2] text-[#CECFD4]'
              : 'bg-[#E5E6EB] text-[#292A33]'
          )}
        >
          <ChevronIcon className="h-6 w-6" aria-hidden />
        </button>
      </div>
    </section>
  );
};

const Paywall3Layout = ({
  isOnlyPayment,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  recurringAdjective,
  renewalPriceText,
  documentName,
  documentExtension,
  documentFile,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    isOnlyPayment: boolean;
    hasTrial: boolean;
    trialDurationText: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    recurringPeriodText: string;
    recurringAdjective: string;
    renewalPriceText: string;
  }) => {
  const trialSegment = hasTrial && trialDurationText ? trialDurationText : '';
  const includesTitle = trialSegment
    ? `Your ${trialSegment} trial includes:`
    : 'Your subscription includes:';
  const renewalFeature = trialSegment
    ? `After the ${trialSegment} trial, your plan renews at ${renewalPriceText}`
    : `Your plan renews at ${renewalPriceText}`;

  return (
    <div className="min-h-full bg-[#F6F7F8] font-barlow text-[#292A33]">
      <header
        className={`mx-auto flex w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} items-center justify-between px-8 py-5`}
      >
        <img
          src="/images/logo/brand.svg"
          alt="PDF Genius"
          className="h-8 w-[136px]"
        />
        {!isOnlyPayment && (
          <div className="hidden items-center gap-3 lg:flex">
            <Step state="complete" label="File is ready" />
            <StepLine />
            <Step state="active" index={2} label="Payment details" />
            <StepLine />
            <Step state="inactive" index={3} label="Download" />
          </div>
        )}
      </header>

      <div
        className={`mx-auto grid w-full ${PAYWALL_CONTENT_MAX_WIDTH_CLASS} grid-cols-1 items-start gap-4 px-4 pb-6 lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-x-8 lg:pb-8`}
      >
        <section className="order-2 flex w-full flex-col gap-1 rounded-[12px] bg-white p-4 lg:order-none lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:px-8 lg:py-6">
          <div className="flex w-full flex-col gap-2">
            <TotalDueRow amountText={dueTodayAmountText} />
            <PaywallDivider />
          </div>

          {checkoutMethod && (
            <div className="relative">
              <PaywallCheckoutFrame
                checkoutContainerRef={checkoutContainerRef}
                paymentStatus={paymentStatus}
              />
            </div>
          )}

          <div className="flex w-full flex-col gap-4 pt-2">
            <p className="text-[14px] leading-5 text-[#292A33]">
              By continuing, you agree that you will be charged{' '}
              <strong className="font-semibold">{dueTodayAmountText}</strong>{' '}
              {trialSegment ? (
                <>
                  today for a {trialSegment} trial and{' '}
                  <strong className="font-semibold">
                    {recurringAmountText}
                  </strong>{' '}
                  automatically{' '}
                  <strong className="font-semibold">
                    every {recurringPeriodText}
                  </strong>{' '}
                  thereafter unless you cancel before your trial ends.
                </>
              ) : (
                <>
                  today and automatically{' '}
                  <strong className="font-semibold">
                    every {recurringPeriodText}
                  </strong>{' '}
                  unless you cancel.
                </>
              )}{' '}
              You can cancel your auto-renewing subscription through your
              online account before your next {recurringAdjective} renewal
              date. You also agree to our{' '}
              <Link
                href={PAYWALL_LINKS.subscription}
                target="_blank"
                className="font-semibold"
              >
                Subscription Policy
              </Link>{' '}
              and{' '}
              <Link
                href={PAYWALL_LINKS.refund}
                target="_blank"
                className="font-semibold"
              >
                Refund Policy
              </Link>
              .
            </p>
            <PaywallDivider />
            <div className="flex flex-wrap gap-2">
              {PAYWALL_TRUST_BADGES.map(({ label, Icon }) => (
                <span
                  key={label}
                  className="inline-flex items-center justify-center gap-1 rounded-[6px] bg-[#DAF6ED] px-1.5 py-1 text-[11px] font-semibold uppercase text-[#009E5E]"
                >
                  <Icon
                    className="h-4 w-4 shrink-0 text-[#00A170]"
                    aria-hidden
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {!isOnlyPayment && (
          <div className="order-1 w-full lg:order-none lg:col-start-2">
            <DocumentReadyCard
              documentName={documentName}
              documentExtension={documentExtension}
              documentFile={documentFile}
            />
          </div>
        )}

        <div className="order-3 w-full lg:order-none lg:col-start-2">
          <div className="flex w-full flex-col gap-4 rounded-[12px] bg-white px-4 py-6 lg:px-8">
            <div className="flex w-full flex-col gap-4 lg:gap-6">
              <h3 className="text-[18px] font-bold text-[#292A33]">
                {includesTitle}
              </h3>
              <ul className="flex w-full flex-col gap-2.5">
                {PAYWALL_PLAN_FEATURES.map((feature) => (
                  <li className="flex w-full items-center gap-2" key={feature}>
                    <CircleCheckIcon
                      className="h-6 w-6 shrink-0 text-[#016FD0]"
                      aria-hidden
                    />
                    <span className="text-[18px] text-[#292A33]">
                      {feature}
                    </span>
                  </li>
                ))}
                <li className="flex w-full items-center gap-2">
                  <CircleCheckIcon
                    className="h-6 w-6 shrink-0 text-[#016FD0]"
                    aria-hidden
                  />
                  <span className="text-[18px] font-medium text-[#292A33]">
                    {renewalFeature}
                  </span>
                </li>
              </ul>
            </div>
            <PaywallDivider />
            <TotalDueRow amountText={dueTodayAmountText} />
          </div>
        </div>
      </div>

      <Testimonials />
    </div>
  );
};

export default Paywall3Layout;
